import { View, Text } from "react-native";
import { ClipboardCheck, ShieldCheck, Users } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import type { FETParticipantView } from "@src/packages/cycles/useFETParticipantViews";

type ParticipantView = FETParticipantView;
type TaskKey = ParticipantView["tasks"][number]["taskKey"];

const TASK_LABEL_KEYS: Record<TaskKey, string> = {
  recipient_medications: "homepage.fet_participant_view.task_medications",
  recipient_monitoring: "homepage.fet_participant_view.task_monitoring",
  transfer_acknowledgement:
    "homepage.fet_participant_view.task_transfer_acknowledgement",
  beta_hcg_collection: "homepage.fet_participant_view.task_beta_collection",
  recipient_consent: "homepage.fet_participant_view.task_recipient_consent",
  embryo_authorization:
    "homepage.fet_participant_view.task_embryo_authorization",
  backup_decision: "homepage.fet_participant_view.task_backup_decision",
  clinic_coordination: "homepage.fet_participant_view.task_clinic_coordination",
  transport_coordination:
    "homepage.fet_participant_view.task_transport_coordination",
  storage_chain_of_custody:
    "homepage.fet_participant_view.task_storage_chain_of_custody",
  result_review: "homepage.fet_participant_view.task_result_review",
};

function roleLabel(role: string, t: ReturnType<typeof useTranslations>) {
  switch (role) {
    case "recipient":
      return t("homepage.fet_participant_view.role_recipient");
    case "partner":
      return t("homepage.fet_participant_view.role_partner");
    case "intended-parent":
      return t("homepage.fet_participant_view.role_intended_parent");
    case "gestational-carrier":
      return t("homepage.fet_participant_view.role_gestational_carrier");
    case "donor":
      return t("homepage.fet_participant_view.role_donor");
    case "genetic-source":
      return t("homepage.fet_participant_view.role_genetic_source");
    case "care-coordinator":
      return t("homepage.fet_participant_view.role_care_coordinator");
    case "custodian":
      return t("homepage.fet_participant_view.role_custodian");
    default:
      return t("homepage.fet_participant_view.role_not_reported");
  }
}

export function FETParticipantHomeView({ view }: { view: ParticipantView }) {
  const t = useTranslations();
  const assignedTasks = view.tasks.filter(
    (task) => task.ownerParticipantId === view.viewerParticipantId,
  );
  const currentRole = view.roles[0]?.role ?? view.recipientRole;

  return (
    <View testID="fet-participant-home" style={{ gap: 16 }}>
      <View style={{ flexDirection: "row", gap: 10, alignItems: "flex-start" }}>
        <Users size={22} color={EMBER.colors.teal} />
        <View style={{ flex: 1, gap: 4 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 17,
              lineHeight: 23,
              color: EMBER.colors.text,
              fontWeight: "600",
            }}
          >
            {t("homepage.fet_participant_view.shared_plan_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 20,
              color: EMBER.colors.textMid,
            }}
          >
            {t("homepage.fet_participant_view.shared_plan_body")}
          </Text>
        </View>
      </View>

      <View
        style={{
          paddingTop: 12,
          borderTopWidth: 1,
          borderTopColor: EMBER.colors.border,
          gap: 8,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 19,
            color: EMBER.colors.text,
            fontWeight: "600",
          }}
        >
          {t("homepage.fet_participant_view.role_summary", {
            params: { role: roleLabel(currentRole, t) },
          })}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 18,
            color: EMBER.colors.textMid,
          }}
        >
          {t("homepage.fet_participant_view.plan_status", {
            params: { status: view.planStatus },
          })}
        </Text>
      </View>

      <View style={{ gap: 9 }}>
        <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
          <ClipboardCheck size={17} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 20,
              color: EMBER.colors.text,
              fontWeight: "600",
            }}
          >
            {t("homepage.fet_participant_view.tasks_section")}
          </Text>
        </View>
        {assignedTasks.length > 0 ? (
          assignedTasks.map((task) => (
            <View
              key={task.taskKey}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderRadius: 12,
                backgroundColor: "rgba(255, 255, 255, 0.46)",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 19,
                  color: EMBER.colors.text,
                }}
              >
                {t(TASK_LABEL_KEYS[task.taskKey])}
              </Text>
            </View>
          ))
        ) : (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
            }}
          >
            {t("homepage.fet_participant_view.no_assigned_tasks")}
          </Text>
        )}
      </View>

      {view.consentStatuses.length > 0 ? (
        <View style={{ gap: 8 }}>
          <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
            <ShieldCheck size={17} color={EMBER.colors.teal} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 20,
                color: EMBER.colors.text,
                fontWeight: "600",
              }}
            >
              {t("homepage.fet_participant_view.consent_section")}
            </Text>
          </View>
          {view.consentStatuses.map((consent) => (
            <Text
              key={`${consent.type}-${consent.status}`}
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
              }}
            >
              {t("homepage.fet_participant_view.consent_status", {
                params: { status: consent.status },
              })}
            </Text>
          ))}
        </View>
      ) : null}
    </View>
  );
}
